import React, { useEffect, useState, useCallback } from 'react';
import { api, endpoints } from '../config/api';
import { Card, Badge, Alert, Button, Input } from '../components/ui';
import { BRAND_ASSET_TYPE_LABELS } from '../constants/brandAssetTypes';
import { useT, pick, apiErrorMessage } from '../i18n';
import './BrandAssets.css';

const messages = {
  docTitle: { tr: 'Marka Varlıkları - Signature Manager', en: 'Brand Assets - Signature Manager' },
  colorsLoadFailed: { tr: 'Marka renkleri yüklenemedi', en: 'Couldn’t load brand colors' },
  colorNameRequired: { tr: 'Renk adı gerekli', en: 'Color name is required' },
  invalidHex: { tr: 'Geçerli bir hex renk kodu girin (Örn. #0e22f0)', en: 'Enter a valid hex color code (e.g. #0e22f0)' },
  colorAddFailed: { tr: 'Renk eklenemedi', en: 'Couldn’t add the color' },
  colorDeleteFailed: { tr: 'Renk silinemedi', en: 'Couldn’t delete the color' },
  assetsLoadFailed: { tr: 'Marka varlıkları yüklenemedi', en: 'Couldn’t load brand assets' },
  uploadFailed: { tr: 'Yükleme başarısız oldu', en: 'Upload failed' },
  confirmDelete: { tr: 'Bu marka varlığını silmek istediğinize emin misiniz?', en: 'Are you sure you want to delete this brand asset?' },
  deleteFailed: { tr: 'Silme başarısız oldu', en: 'Delete failed' },
  nameRequired: { tr: 'Ad gerekli', en: 'Name is required' },
  updateFailed: { tr: 'Güncelleme başarısız oldu', en: 'Update failed' },
  title: { tr: 'Marka Varlıkları', en: 'Brand Assets' },
  hint: {
    tr: 'Logo, ikon ve banner görsellerinizi buraya yükleyin — imza, yasal uyarı ve kampanya düzenleyicisindeki görsel ekleme penceresinde bu kütüphaneden seçebilirsiniz, her seferinde dışarıdan bir URL yapıştırmanıza gerek kalmaz.',
    en: 'Upload your logo, icon and banner images here — you can pick them from this library in the image dialog of the signature, disclaimer and campaign editors, instead of pasting an external URL every time.',
  },
  colorsTitle: { tr: 'Marka Renkleri', en: 'Brand Colors' },
  colorsHint: {
    tr: 'Bu renkler, imza/yasal uyarı/kampanya düzenleyicisindeki metin rengi paletinde en başta, verdiğiniz adla sunulur — her seferinde hex kodu hatırlamanıza gerek kalmaz.',
    en: 'These colors appear first, under the names you give them, in the text color palette of the signature/disclaimer/campaign editors — no need to remember hex codes.',
  },
  deleteColorAria: { tr: '{name} rengini sil', en: 'Delete color {name}' },
  newColorPicker: { tr: 'Yeni renk seçici', en: 'New color picker' },
  colorName: { tr: 'Renk Adı', en: 'Color Name' },
  colorNamePlaceholder: { tr: 'Örn. Ana Mavi', en: 'E.g. Primary Blue' },
  hexCode: { tr: 'Hex Kod', en: 'Hex Code' },
  adding: { tr: 'Ekleniyor...', en: 'Adding...' },
  addColor: { tr: '+ Renk Ekle', en: '+ Add Color' },
  assetNamePlaceholder: { tr: 'Örn. Ana Logo', en: 'E.g. Main Logo' },
  file: { tr: 'Dosya (görsel, azami 5MB)', en: 'File (image, max 5MB)' },
  uploading: { tr: 'Yükleniyor...', en: 'Uploading...' },
  upload: { tr: 'Yükle', en: 'Upload' },
  noAssets: { tr: 'Henüz bir marka varlığı yüklenmedi.', en: 'No brand assets uploaded yet.' },
  type: { tr: 'Tür', en: 'Type' },
};

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export default function BrandAssets() {
  const { t, lang } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  // Marka Rengi — a named list (BrandColor model) an org_admin builds up,
  // not a single Organization.primary_color field (that only let an org
  // name one color; a real brand kit needs several — accent, secondary,
  // etc. — each with its own name). Every color here becomes an extra
  // swatch in the forecolor/backcolor palette of every email-facing editor,
  // see useOrganizationBrandColors/emailEditorConfig.js.
  const [brandColors, setBrandColors] = useState([]);
  const [brandColorsLoading, setBrandColorsLoading] = useState(true);
  const [brandColorsError, setBrandColorsError] = useState(null);
  const [newColorName, setNewColorName] = useState('');
  const [newColorHex, setNewColorHex] = useState('#0e22f0');
  const [addingColor, setAddingColor] = useState(false);

  const fetchBrandColors = useCallback(async () => {
    try {
      setBrandColorsLoading(true);
      setBrandColorsError(null);
      const data = await api.get(endpoints.signatures.brandColors.list);
      setBrandColors(data || []);
    } catch (err) {
      console.error('Error fetching brand colors:', err);
      setBrandColorsError(pick(messages.colorsLoadFailed));
    } finally {
      setBrandColorsLoading(false);
    }
  }, []);

  useEffect(() => { fetchBrandColors(); }, [fetchBrandColors]);

  const handleAddColor = async (e) => {
    e.preventDefault();
    if (!newColorName.trim()) {
      setBrandColorsError(t('colorNameRequired'));
      return;
    }
    if (!HEX_COLOR_PATTERN.test(newColorHex)) {
      setBrandColorsError(t('invalidHex'));
      return;
    }
    try {
      setAddingColor(true);
      setBrandColorsError(null);
      await api.post(endpoints.signatures.brandColors.list, { name: newColorName.trim(), hex_color: newColorHex });
      setNewColorName('');
      setNewColorHex('#0e22f0');
      await fetchBrandColors();
    } catch (err) {
      console.error('Error adding brand color:', err);
      setBrandColorsError((lang === 'tr' && (err.response?.data?.hex_color?.[0] || err.response?.data?.name?.[0])) || t('colorAddFailed'));
    } finally {
      setAddingColor(false);
    }
  };

  const handleDeleteColor = async (id) => {
    try {
      await api.delete(endpoints.signatures.brandColors.detail(id));
      setBrandColors(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error deleting brand color:', err);
      setBrandColorsError(t('colorDeleteFailed'));
    }
  };

  const [brandAssets, setBrandAssets] = useState([]);
  const [brandAssetsLoading, setBrandAssetsLoading] = useState(true);
  const [brandAssetsError, setBrandAssetsError] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Ad/tür artık yükleme sırasında değil, yükledikten sonra "Düzenle" ile
  // ayarlanıyor — bkz. handleSaveAssetEdit. Tür varsayılan olarak "Diğer"
  // gelir (backend brand_assets_list'in kendi varsayılanı); {{BrandLogo}}
  // placeholder'ının kullandığı asset_type='logo' gibi bir tür atamak
  // isteyen admin bunu buradan sonradan yapar.
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('other');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchBrandAssets = useCallback(async () => {
    try {
      setBrandAssetsLoading(true);
      setBrandAssetsError(null);
      const data = await api.get(endpoints.signatures.brandAssets.list);
      setBrandAssets(data || []);
    } catch (err) {
      console.error('Error fetching brand assets:', err);
      setBrandAssetsError(pick(messages.assetsLoadFailed));
    } finally {
      setBrandAssetsLoading(false);
    }
  }, []);

  useEffect(() => { fetchBrandAssets(); }, [fetchBrandAssets]);

  const handleBrandAssetUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    try {
      setUploading(true);
      setBrandAssetsError(null);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName || uploadFile.name);
      await api.post(endpoints.signatures.brandAssets.list, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadName('');
      setUploadFile(null);
      e.target.reset?.();
      await fetchBrandAssets();
    } catch (err) {
      console.error('Error uploading brand asset:', err);
      setBrandAssetsError(apiErrorMessage(err, messages.uploadFailed));
    } finally {
      setUploading(false);
    }
  };

  const handleBrandAssetDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(endpoints.signatures.brandAssets.detail(id));
      setBrandAssets(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Error deleting brand asset:', err);
      setBrandAssetsError(t('deleteFailed'));
    }
  };

  const startEditAsset = (asset) => {
    setEditingAssetId(asset.id);
    setEditName(asset.name);
    setEditType(asset.asset_type);
  };

  const cancelEditAsset = () => setEditingAssetId(null);

  const handleSaveAssetEdit = async (e, id) => {
    e.preventDefault();
    if (!editName.trim()) {
      setBrandAssetsError(t('nameRequired'));
      return;
    }
    try {
      setSavingEdit(true);
      setBrandAssetsError(null);
      await api.patch(endpoints.signatures.brandAssets.detail(id), { name: editName.trim(), asset_type: editType });
      setEditingAssetId(null);
      await fetchBrandAssets();
    } catch (err) {
      console.error('Error updating brand asset:', err);
      setBrandAssetsError(apiErrorMessage(err, messages.updateFailed));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="page-stack">
      <h1>{t('title')}</h1>
      <p className="brand-assets-hint">{t('hint')}</p>

      <Card className="brand-assets-card brand-color-card">
        <h2 className="brand-assets-section-title">{t('colorsTitle')}</h2>
        <p className="brand-assets-hint">{t('colorsHint')}</p>

        {brandColorsLoading ? (
          <p className="brand-assets-inline-loading">{t('loading')}</p>
        ) : brandColors.length > 0 && (
          <ul className="brand-color-list">
            {brandColors.map(color => (
              <li className="brand-color-list-item" key={color.id}>
                <span className="brand-color-swatch" style={{ background: color.hex_color }} />
                <span className="brand-color-name">{color.name}</span>
                <span className="brand-color-hex">{color.hex_color}</span>
                <button
                  type="button"
                  className="brand-color-remove-btn"
                  onClick={() => handleDeleteColor(color.id)}
                  aria-label={t('deleteColorAria', { name: color.name })}
                  title={t('delete')}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={handleAddColor} className="brand-color-form">
          <input
            type="color"
            className="brand-color-swatch-input"
            value={newColorHex}
            onChange={e => setNewColorHex(e.target.value)}
            aria-label={t('newColorPicker')}
          />
          <Input
            label={t('colorName')}
            type="text"
            value={newColorName}
            onChange={e => setNewColorName(e.target.value)}
            placeholder={t('colorNamePlaceholder')}
          />
          <Input
            label={t('hexCode')}
            type="text"
            value={newColorHex}
            onChange={e => setNewColorHex(e.target.value)}
            placeholder="#0e22f0"
          />
          <Button type="submit" disabled={addingColor}>
            {addingColor ? t('adding') : t('addColor')}
          </Button>
        </form>
        {brandColorsError && <Alert variant="error">{brandColorsError}</Alert>}
      </Card>

      <Card className="brand-assets-card">
        <form onSubmit={handleBrandAssetUpload} className="brand-asset-upload-form">
          <Input
            label={t('name')}
            type="text"
            value={uploadName}
            onChange={e => setUploadName(e.target.value)}
            placeholder={t('assetNamePlaceholder')}
          />
          <div className="form-group">
            <label>{t('file')}</label>
            <input type="file" accept="image/*" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
          </div>
          <Button type="submit" disabled={uploading || !uploadFile}>
            {uploading ? t('uploading') : t('upload')}
          </Button>
        </form>

        {brandAssetsError && <Alert variant="error">{brandAssetsError}</Alert>}

        {brandAssetsLoading ? (
          <p className="brand-assets-inline-loading">{t('loading')}</p>
        ) : brandAssets.length === 0 ? (
          <p className="brand-assets-hint">{t('noAssets')}</p>
        ) : (
          <div className="brand-asset-grid">
            {brandAssets.map(asset => (
              <div className="brand-asset-card" key={asset.id}>
                <img src={asset.url} alt={asset.name} />
                {editingAssetId === asset.id ? (
                  <form className="brand-asset-edit-form" onSubmit={e => handleSaveAssetEdit(e, asset.id)}>
                    <input
                      type="text"
                      className="brand-asset-edit-name"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      aria-label={t('name')}
                      autoFocus
                    />
                    <select
                      className="brand-asset-edit-type"
                      value={editType}
                      onChange={e => setEditType(e.target.value)}
                      aria-label={t('type')}
                    >
                      {Object.entries(BRAND_ASSET_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{pick(label, undefined, lang)}</option>
                      ))}
                    </select>
                    <div className="brand-asset-edit-actions">
                      <Button type="submit" disabled={savingEdit}>
                        {savingEdit ? t('saving') : t('save')}
                      </Button>
                      <Button type="button" variant="secondary" onClick={cancelEditAsset}>{t('cancel')}</Button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="brand-asset-card-body">
                      <span className="brand-asset-card-name">{asset.name}</span>
                      <Badge variant="info">{BRAND_ASSET_TYPE_LABELS[asset.asset_type] ? pick(BRAND_ASSET_TYPE_LABELS[asset.asset_type], undefined, lang) : asset.asset_type}</Badge>
                    </div>
                    <div className="brand-asset-card-actions">
                      <Button variant="secondary" onClick={() => startEditAsset(asset)}>
                        {t('edit')}
                      </Button>
                      <Button variant="danger" onClick={() => handleBrandAssetDelete(asset.id)}>
                        {t('delete')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
